import debounce from 'lodash/debounce';
class DOMUtils {
  static getElement<T extends Element>(parent: ParentNode, selector: string): T {
    const element = parent.querySelector(selector) as T | null;
    if (!element) {
      throw new Error(`Element not found for selector: ${selector}`);
    }
    return element;
  }

  static getElements<T extends Element>(parent: ParentNode, selector: string): T[] {
    const elements = Array.from(parent.querySelectorAll(selector)) as T[];
    if (elements.length === 0) {
      console.error(`No elements found for selector: ${selector}`);
    }
    return elements;
  }
}
class CommunityContentAdvancedSearch {
  static readonly CATEGORY_NAME = 'search_community_content_advanced[category_names][]';
  static readonly MENU_ROOT = '.js-custom-menu-select-component';
  static readonly CATEGORY_ROOT = '.js-custom-multi-select-component';
  static readonly SORT_ROOT = '.js-custom-select-component';
  static readonly RESULT_SORT_ROOT = '.js-custom-result-sort-select-root';

  private openedCustomSelect: {
    customSelect: HTMLDivElement,
    button: HTMLButtonElement
  } | null = null;

  constructor() {
    this.addSubmitEventListener();
    this.addRefineSearchToggleEventListener();
    this.addSortSelectEventListeners();
    this.addRefineSelectEventListener();
    this.addCancellationListener();
    this.addResultSortSelectEventListener();
  }

  addResultSortSelectEventListener() {
    const root = document.querySelector(CommunityContentAdvancedSearch.RESULT_SORT_ROOT);
    if (!root) return;

    const selects = DOMUtils.getElements<HTMLSelectElement>(root, 'select');
    selects.forEach((select) => {
      const name = select.getAttribute('name');
      if (!name) throw new Error('select要素にname属性がありません');
      select.addEventListener('change', (e) => {
        const currentUrl = new URL(window.location.href);
        const sortValue = (e.target as HTMLSelectElement).value;
        currentUrl.searchParams.set(`search_community_content_advanced[${name}]`, sortValue);
        location.assign(currentUrl.toString());
      });
    });
  }

  addCancellationListener() {
    document.addEventListener('click', (e) => {
      if (!this.openedCustomSelect) return;
      const { customSelect, button } = this.openedCustomSelect;

      // クリックが開いているカスタムセレクト内なら何もしない
      if (customSelect.contains(e.target as Node) || button.contains(e.target as Node)) return;
      customSelect.classList.add('hidden');
      button.setAttribute('aria-expanded', 'false');
      this.openedCustomSelect = null;
    });
  }

  addSubmitEventListener() {
    const root = DOMUtils.getElement<HTMLDivElement>(document, '.js-search-button');
    const button = DOMUtils.getElement<HTMLButtonElement>(root, 'button[type="button"]');
    const form = root.closest('form') as HTMLFormElement;

    button.addEventListener('click', (e) => {
      e.preventDefault();
      form.submit();
    });
  }

  addRefineSearchToggleEventListener() {
    const root = DOMUtils.getElement<HTMLDivElement>(document, '.js-search-refine-toggle');
    const button = DOMUtils.getElement<HTMLButtonElement>(root, 'button');

    button.addEventListener('click', (e) => {
      e.preventDefault();
      const isExpanded = root.getAttribute('aria-expanded') === 'true';
      root.setAttribute('aria-expanded', String(!isExpanded));
    });
  }

  _setCustomSelectText(buttonLabel: HTMLSpanElement, select: HTMLSelectElement) {
    const selectedOptions = Array.from(select.selectedOptions);
    if (selectedOptions.length > 0) {
      buttonLabel.textContent = selectedOptions
        .map(option => option.label)
        .sort((a, b) => a.localeCompare(b, 'ja'))
        .join(', ');
    } else {
      buttonLabel.textContent = select.getAttribute('data-initial-text');
    }
  }

  _changeMenuOptionsDisabledState() {
    const components = document.querySelectorAll(CommunityContentAdvancedSearch.MENU_ROOT);
    const selectedMenuIds = Array.from(components).map((component) => {
      const select = DOMUtils.getElement<HTMLSelectElement>(component, 'select');
      return select.value;
    });

    // 選択肢を無効化
    components.forEach((component) => {
      const select = DOMUtils.getElement<HTMLSelectElement>(component, 'select');
      const options = DOMUtils.getElements<HTMLOptionElement>(select, 'option');
      const customSelect = DOMUtils.getElement<HTMLDivElement>(component, '.custom-select');
      const customOptions = DOMUtils.getElements<HTMLDivElement>(customSelect, '[data-value]');
      const optionGroups = select.querySelectorAll('optgroup');
      const customOptionGroups = component.querySelectorAll('.custom-select-option-group');

      options.forEach((option) => {
        if (selectedMenuIds.includes(option.value) && !option.selected && option.value !== '') {
          option.disabled = true;
        } else {
          option.disabled = false;
        }
      });

      optionGroups.forEach((group) => {
        const groupOptions = group.querySelectorAll('option');
        // グループ内のすべてのオプションが無効化されている場合、グループを非表示にする
        if (Array.from(groupOptions).every((option) => option.disabled || option.selected)) {
          group.classList.add('hidden');
        } else {
          group.classList.remove('hidden');
        }
      });

      customOptions.forEach((option) => {
        const value = option.getAttribute('data-value');
        if (!value) return;

        if (selectedMenuIds.includes(value) && value !== '') {
          option.classList.add('hidden');
        } else {
          option.classList.remove('hidden');
        }
      });

      customOptionGroups.forEach((group) => {
        const groupOptions = group.querySelectorAll('[data-value]');
        // グループ内のすべてのオプションが無効化されている場合、グループを非表示にする
        if (Array.from(groupOptions).every((option) => option.classList.contains('hidden'))) {
          group.classList.add('hidden');
        } else {
          group.classList.remove('hidden');
        }
      });
    });
  }

  _fetchCategoryOptions(
    parent: HTMLDivElement,
    hiddenInput: HTMLInputElement | null,
    value: string = '',
    isInit: boolean = false
  ) {
    const component = DOMUtils.getElement<HTMLDivElement>(parent, CommunityContentAdvancedSearch.CATEGORY_ROOT);
    const select = DOMUtils.getElement<HTMLSelectElement>(component, 'select');
    const customSelect = DOMUtils.getElement<HTMLDivElement>(component, '.custom-select');
    const button = DOMUtils.getElement<HTMLButtonElement>(component, 'button');
    const buttonLabel = DOMUtils.getElement<HTMLSpanElement>(button, 'span');
    const inner = document.createElement('div');
    const template = DOMUtils.getElement<HTMLTemplateElement>(component, 'template');
    const urlParams = new URLSearchParams(window.location.search);
    const name = `search_community_content_advanced[mc][${value}][]`
    inner.classList.add('py-1');
    select.innerHTML = '';
    customSelect.innerHTML = '';

    if (!value || value === '') {
      button.disabled = true;
      select.disabled = true;
      this._setCustomSelectText(buttonLabel, select);
      select.removeAttribute('name');
      hiddenInput && hiddenInput.removeAttribute('name');
      return;
    }

    select.setAttribute('name', name);
    hiddenInput && hiddenInput.setAttribute('name', name);

    fetch(`/menus/${value}/categories`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.length === 0) {
          // カテゴリが存在しない場合の処理 
          button.disabled = true;
          select.disabled = true;
          buttonLabel.textContent = 'カテゴリなし';
        } else {
          // 新しいカテゴリオプションを追加
          data.forEach((category: { value: string; name: string }) => {
            const option = document.createElement('option');
            option.value = category.value;
            option.textContent = category.name;
            select.appendChild(option);

            const clone = template.content.cloneNode(true) as DocumentFragment;
            const spanElement = clone.querySelector('span:nth-child(2)') as HTMLSpanElement;
            const dataValueElement = clone.querySelector('[data-value]') as HTMLElement;

            if (spanElement) {
              spanElement.textContent = category.name;
            }
            if (dataValueElement) {
              dataValueElement.dataset.value = category.value;
            }
            inner.appendChild(clone);
          });

          customSelect.appendChild(inner);

          if (isInit) {
            const selectedCategories = urlParams.getAll(`search_community_content_advanced[mc][${value}][]`);
            Array.from(select.options).forEach((option) => {
              if (selectedCategories.includes(option.value)) {
                option.selected = true;
                const customOption = customSelect.querySelector(`[data-value="${option.value}"]`);
                if (customOption) {
                  customOption.setAttribute('aria-selected', 'true');
                }
              } else {
                option.selected = false;
                const customOption = customSelect.querySelector(`[data-value="${option.value}"]`);
                if (customOption) {
                  customOption.setAttribute('aria-selected', 'false');
                }
              }
            });
          }

          button.disabled = false;
          select.disabled = false;
          this._setCustomSelectText(buttonLabel, select);
        }
      })
      .catch((error) => {
        button.disabled = true;
        select.disabled = true;
        buttonLabel.textContent = '読み込みエラー - 再選択してください';
        console.warn('Error fetching categories:', error);
      });
  }

  _appendNewCustomSelection(value: string, component: HTMLDivElement) {
    const container = DOMUtils.getElement<HTMLDivElement>(document, '.js-appended-nodes');
    const template = DOMUtils.getElement<HTMLTemplateElement>(document, '.select-group-template');
    const components = document.querySelectorAll(CommunityContentAdvancedSearch.MENU_ROOT);
    const hasUnselected = Array.from(components).some((component) => {
      return component.querySelector('select')?.value === '';
    });

    if (value && !hasUnselected && components.length < Number(component.dataset.menuCount)) {
      const newCustomSelect = template.content.cloneNode(true);
      container.appendChild(newCustomSelect);
    }
  }

  addSortSelectEventListeners() {
    const components: NodeListOf<HTMLDivElement> = document.querySelectorAll(CommunityContentAdvancedSearch.SORT_ROOT);

    if (components.length === 0) {
      console.error('No custom select components found.');
      return;
    }

    components.forEach((component) => {
      return this.initializeSelectEventListener(component as HTMLDivElement, {
        onChange: (args) => {
          if (!args) return;
          const { button, customSelect } = args;
          customSelect.classList.add('hidden');
          button.setAttribute('aria-expanded', 'false');
        },
      });
    });
  }

  addMenuSelectEventListener(parent: HTMLDivElement) {
    const component = DOMUtils.getElement<HTMLDivElement>(parent, CommunityContentAdvancedSearch.MENU_ROOT);

    this.initializeSelectEventListener(component, {
      onInit: (args) => {
        if (!args) return;
        const { hiddenInput, select } = args;

        // Menu → Categoryに対しての処理
        this._fetchCategoryOptions(parent, hiddenInput, select.value, true);
        // Menuに対しての処理
        this._changeMenuOptionsDisabledState();
        this._appendNewCustomSelection(select.value, component);
      },
      onChange: (args) => {
        if (!args) return;
        const { hiddenInput, select, button, customSelect } = args;
        customSelect.classList.add('hidden');
        button.setAttribute('aria-expanded', 'false');

        // Menu → Categoryに対しての処理
        this._fetchCategoryOptions(parent, hiddenInput, select.value);
        // Menuに対しての処理
        this._changeMenuOptionsDisabledState();
        this._appendNewCustomSelection(select.value, component);
      },
    });
  }

  addCategorySelectEventListener(parent: HTMLDivElement) {
    const component = DOMUtils.getElement<HTMLDivElement>(parent, CommunityContentAdvancedSearch.CATEGORY_ROOT);
    this.initializeSelectEventListener(component as HTMLDivElement, {});
  }

  addRefineSelectEventListener() {
    document.querySelectorAll('.js-refine-menu-select').forEach((parent) => {
      this.addMenuSelectEventListener(parent as HTMLDivElement);
      this.addCategorySelectEventListener(parent as HTMLDivElement);
    });

    // 非同期で追加された要素に対してもイベントリスナーを追加
    const targetNode = document.querySelector('.js-appended-nodes');
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLDivElement).matches('.js-refine-menu-select')) {
              this.addMenuSelectEventListener(node as HTMLDivElement);
              this.addCategorySelectEventListener(node as HTMLDivElement);
            }
          });
        }
      });
    });

    if (targetNode) {
      observer.observe(targetNode, {
        childList: true
      });
    }
  }

  initializeSelectEventListener(
    component: HTMLDivElement,
    {
      onInit = (_args?: { hiddenInput: HTMLInputElement | null, select: HTMLSelectElement, button: HTMLButtonElement, buttonLabel: HTMLSpanElement, customSelect: HTMLDivElement }) => {},
      onChange = (_args?: { hiddenInput: HTMLInputElement | null, select: HTMLSelectElement, button: HTMLButtonElement, buttonLabel: HTMLSpanElement, customSelect: HTMLDivElement }) => {},
    },
  ) {
    const select = DOMUtils.getElement<HTMLSelectElement>(component, 'select');
    const hiddenInput = component.querySelector('input[type="hidden"]') as HTMLInputElement | null;
    const button = DOMUtils.getElement<HTMLButtonElement>(component, 'button');
    const buttonLabel = DOMUtils.getElement<HTMLSpanElement>(button, 'span');
    const customSelect = DOMUtils.getElement<HTMLDivElement>(component, '.custom-select');

    if (typeof onInit === 'function') {
      // NOTE: addMenuSelectEventListenerではセレクトボックスのイベントリスナを追加と、既存セレクトボックスの同期が必要
      // NOTE: addCategorySelectEventListenerではセレクトボックスのイベントリスナを追加
      onInit({ hiddenInput, select, button, buttonLabel, customSelect });
    }

    select.addEventListener('change', () => {
      // カスタムセレクトのボタンラベルと同期
      this._setCustomSelectText(buttonLabel, select);

      // カスタムセレクトの選択肢と同期
      Array.from(select.options).forEach((option) => {
        const customOption = customSelect.querySelector(`[data-value="${option.value}"]`);
        if (customOption) {
          customOption.setAttribute('aria-selected', option.selected ? 'true' : 'false');
        }
      });

      // NOTE: addSortSelectEventListenersではカスタム選択肢の制御(hidden)が必要
      // NOTE: addMenuSelectEventListenerでは独自の処理と、カスタム選択肢の制御(hidden)が必要
      // NOTE: addMenuSelectEventListenerではセレクトボックスのイベントリスナを追加と、既存セレクトボックスの同期が必要
      onChange({ hiddenInput, select, button, buttonLabel, customSelect });
    });

    window.addEventListener('resize', debounce(() => {
      customSelect.classList.add('hidden');
    }, 200));

    button.addEventListener('click', () => {
      // 開いているカスタムセレクトのみ閉じる
      if (this.openedCustomSelect && this.openedCustomSelect.customSelect !== customSelect) {
        this.openedCustomSelect.customSelect.classList.add('hidden');
        this.openedCustomSelect.button.setAttribute('aria-expanded', 'false');
        this.openedCustomSelect = null;
      }

      // 現在のものだけ開閉
      customSelect.classList.toggle('hidden');
      button.setAttribute('aria-expanded', String(!(button.getAttribute('aria-expanded') === 'true')));

      if (!customSelect.classList.contains('hidden')) {
        this.openedCustomSelect = { customSelect, button };
      } else {
        this.openedCustomSelect = null;
      }
    });

    customSelect.addEventListener('click', (e) => {
      const customOption = (e.target as Element)?.closest('[data-value]');
      if (!customOption) return;

      const value = (customOption as HTMLDivElement).dataset.value;
      if (value === undefined) return;

      const choosedOption = Array.from(select.options).find(opt => opt.value === value);
      if (!choosedOption) return;

      customOption.setAttribute('aria-selected', choosedOption.selected ? 'false' : 'true');
      choosedOption.selected = !choosedOption.selected;

      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CommunityContentAdvancedSearch();
});
